import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, websiteSchema, BRAND } from './SEOUtils';

export default function AboutUnityLovePage() {
  const schemas = [organizationSchema, websiteSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="About UnityLove | Black & Poly Dating App | Our Mission"
        description="UnityLove is Black poly-centered—built for polyamory and polygamy—and welcoming to intentional monogamy. Learn about our mission for conscious connection."
        canonical="/about-unitylove"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            About UnityLove: Dating for Intentional Connection
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            We're building the dating app that Black and poly communities deserve—one that centers authenticity, consent, and meaningful connection over endless swiping.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            UnityLove is Black poly-centered—built for polyamory and polygamy—and welcoming to intentional monogamy rooted in honesty and commitment.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Our Mission
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove exists because we saw a gap. Black singles were underserved by mainstream apps. Polyamorous individuals were treated as edge cases. And those at the intersection—Black poly folks—had almost nowhere to go.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            We're building a different kind of dating platform. One where your identity isn't a filter to apply but the foundation of your experience. Where <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black poly dating</Link> isn't a niche but a celebrated norm.
          </p>
          <p className="text-lg text-slate-700">
            Our mission is simple: create the space for intentional connection that other apps ignore.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            What We Believe
          </h2>
          <div className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Love is Not One-Size-Fits-All</h3>
              <p className="text-slate-700">Intentional monogamy works for some people. Polyamory works for others. Polygamy, open relationships, relationship anarchy—all valid. UnityLove supports how you choose to love.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Representation Matters</h3>
              <p className="text-slate-700">Black users shouldn't have to navigate algorithms optimized for other demographics. We center Black experiences because Black love deserves its own space.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Consent is Non-Negotiable</h3>
              <p className="text-slate-700">Every feature we build considers consent, boundaries, and communication. Healthy relationships—of any structure—require these foundations.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Quality Over Quantity</h3>
              <p className="text-slate-700">We'd rather facilitate one meaningful connection than a hundred meaningless swipes. Our features encourage depth, not volume.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Who UnityLove Is For
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove welcomes anyone seeking intentional connection, with special focus on:
          </p>
          <ul className="space-y-3 text-lg text-slate-700">
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Black singles</strong> looking for partners who share cultural understanding</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Polyamorous individuals</strong> seeking ethical multi-partner connections</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Polygamous families</strong> building structured multi-spouse commitment</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Couples</strong> exploring ethical non-monogamy together</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Intentional monogamists</strong> who value consent-focused community</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span><strong>Anyone</strong> tired of superficial dating and ready for something real</span>
            </li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Our Story
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove was born from frustration. The founders—Black, poly, and tired of being afterthoughts—decided to build what they wished existed. Not another dating app with a polyamory filter, but a platform designed from day one for non-traditional relationships.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            We launched with a simple question: what would a dating app look like if it truly understood us? The answer became UnityLove—a platform where Black poly dating isn't a feature request but the core experience.
          </p>
          <p className="text-lg text-slate-700">
            Today, we're building a community for people who share our vision for conscious, intentional dating — and early members are helping shape what UnityLove becomes.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Key Features
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Value-Based Matching</h3>
              <p className="text-slate-700 text-sm">Connect based on what matters—relationship goals, values, and compatibility.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Speed Dating Events</h3>
              <p className="text-slate-700 text-sm">Virtual events for quick, meaningful connections with vetted members.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Video Lounge</h3>
              <p className="text-slate-700 text-sm">Casual video encounters to build chemistry before committing to a date.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Relationship Style Options</h3>
              <p className="text-slate-700 text-sm">Profile settings for every ENM structure—poly, open, couples, and more.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Voice & Video Messaging</h3>
              <p className="text-slate-700 text-sm">Go beyond text to build real connection before meeting.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-4">
              <h3 className="font-semibold text-slate-900">Community Rooms</h3>
              <p className="text-slate-700 text-sm">Join discussions and events with like-minded members.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Join the Movement</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Be part of a community that's redefining what dating can be. Create your free UnityLove profile today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Sign Up Free
            </Link>
            <a
              href={BRAND.appStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors"
            >
              Download the App
            </a>
          </div>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Explore UnityLove</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/black-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Dating App</Link>
            <Link to="/poly-dating-app" className="text-rose-600 hover:text-rose-700 underline">Poly Dating App</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">ENM Dating</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. Dating for intentional connection.</p>
          <p className="mt-2 text-sm">
            <a href={`mailto:${BRAND.email}`} className="hover:text-slate-900">Contact Us</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
